/**
 * @file        src/pages/hotel/cells.jsx
 * @description Shared table-cell renderers + formatters for the B2C Hotel admin
 *              lists (money, dates, status tone, Guest-aware customer cell, CSV
 *              export). Mirrors pages/flight/cells so both modules read alike.
 * @author      Kinetika Labs
 * @created     2026-07-12
 * @changelog
 *   - 2026-07-12 Claude — Initial (B2C Hotel Management: legacy parity).
 *   - 2026-08-10 Claude — Re-exports lib/docAccess: one shared rule for which
 *                          document actions a booking status allows (drawer,
 *                          row menus and the doc pages themselves all read it).
 *   - 2026-10-06 Claude — statusLabel(): the chip prints the SAME word the stat
 *                          cards and the storefront print (Booked → Success, the
 *                          three unsettled states → Pending). It printed the raw
 *                          column, so one booking read "Awaiting confirmation"
 *                          here and "Pending" on the customer's My Bookings.
 */
import Chip from '../../components/Chip';

export const money = (v) => (v == null || v === '' ? '—' : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export const idCell = (v) => (v == null ? '—' : <span className="mono">#{v}</span>);

/** "25 Sep 2023" — date only (check-in / check-out / validity). */
export const day = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** "01:55 PM , 23 Sep 2023" — the legacy console's booking/updated timestamp. */
export const dateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const t = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${t} , ${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`;
};

/** Chip tone for a booking / refund status. */
export const statusTone = (s = '') =>
  /cancel|fail|reject/i.test(s) ? 'rej'
    : /pend|initiat|await|process/i.test(s) ? 'pend'
      : /book|success|complete|refund/i.test(s) ? 'ok'
        : 'warn';

/**
 * The word a booking status is SHOWN as — identical to the stat cards above the
 * list and to the storefront's pill, so one booking reads one word everywhere.
 * Display only: filters, exports and the update modal keep the stored value.
 */
export const statusLabel = (s = '') => {
  const v = String(s || '').trim();
  if (/^booked$/i.test(v)) return 'Success';
  if (/^(initiated|awaiting confirmation|pending)$/i.test(v)) return 'Pending';
  return v;
};

// One client-approved status→documents rule, shared with the flight module.
export { docAccess } from '../../lib/docAccess';

/**
 * Customer identity. "Guest" marks a booking/search made WITHOUT logging in —
 * detected from user_id, never from a missing name (a registered customer who
 * never saved a name is not a guest).
 */
export function customerCell(_v, r) {
  const name = String(r.customer_name || '').trim();
  const email = r.customer_email || '';
  const isGuest = !r.user_id || Number(r.user_id) === 1;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div>
        {name ? <div>{name}</div> : null}
        {email ? <div className={name ? 'muted' : undefined} style={name ? { fontSize: '.85em' } : undefined}>{email}</div> : null}
        {!name && !email ? <span className="muted">—</span> : null}
      </div>
      {isGuest ? <Chip tone="warn">Guest</Chip> : null}
    </div>
  );
}

/** Download `rows` as a CSV file. `cols` is [{ key, label, get?(row) }]. */
export function downloadCsv(filename, cols, rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [cols.map((c) => esc(c.label)).join(',')];
  for (const r of rows) {
    lines.push(cols.map((c) => esc(c.get ? c.get(r) : r[c.key])).join(','));
  }
  const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
